import api, { ApiError } from './api';

const toIsoDateString = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().toISOString();
  }

  if (typeof value._seconds === 'number') {
    return new Date(value._seconds * 1000).toISOString();
  }

  return null;
};

const toDateInputValue = (value) => {
  const isoString = toIsoDateString(value);
  if (!isoString) {
    return '';
  }

  return isoString.slice(0, 10);
};

const calculateDaysUntil = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  const diffInMs = date.getTime() - today.getTime();
  return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
};

const buildSpecifications = (vehicle = {}) => {
  const entries = {
    engine: vehicle.engine,
    transmission: vehicle.transmission,
    fuelType: vehicle.fuelType,
    gvwr: vehicle.gvwr ? `${vehicle.gvwr}` : undefined,
    emptyWeight: vehicle.emptyWeight ? `${vehicle.emptyWeight}` : undefined,
    capacity: vehicle.capacity ? `${vehicle.capacity}` : undefined,
    wheelbase: vehicle.wheelbase ? `${vehicle.wheelbase}` : undefined,
    sleeper: typeof vehicle.sleeper === 'boolean' ? (vehicle.sleeper ? 'Yes' : 'No') : undefined,
    axles: vehicle.axles,
    color: vehicle.color
  };

  return Object.fromEntries(
    Object.entries(entries).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
};

const mapVehicleFromApi = (vehicle = {}) => {
  const assignedDriverName = vehicle.assignedDriverName
    || vehicle.assignedDriver
    || vehicle.assignedDriverFullName
    || '';

  const location = vehicle.location
    || vehicle.currentLocation
    || vehicle.lastKnownLocation?.name
    || '';

  const formattedLocation = typeof location === 'string'
    ? location
    : [location?.city, location?.state].filter(Boolean).join(', ');

  return {
    id: vehicle.id,
    vehicleNumber: vehicle.vehicleNumber || '',
    unitNumber: vehicle.unitNumber || '',
    make: vehicle.make || '',
    model: vehicle.model || '',
    year: vehicle.year || new Date().getFullYear(),
    vin: vehicle.vin || '',
    type: vehicle.vehicleType || vehicle.type || 'truck',
    status: vehicle.status || 'active',
    purchaseDate: toDateInputValue(vehicle.purchaseDate),
    purchasePrice: vehicle.purchasePrice || '',
    licensePlate: vehicle.licensePlate || '',
    plateState: vehicle.plateState || vehicle.registrationState || '',
    registrationNumber: vehicle.registrationNumber || '',
    registrationState: vehicle.registrationState || vehicle.plateState || '',
    registrationExpiry: toDateInputValue(vehicle.registrationExpiry),
    titleNumber: vehicle.titleNumber || '',
    lienHolder: vehicle.lienHolder || '',
    insuranceProvider: vehicle.insuranceProvider || '',
    policyNumber: vehicle.policyNumber || '',
    insuranceExpiry: toDateInputValue(vehicle.insuranceExpiry),
    coverageAmount: vehicle.coverageAmount || '',
    deductible: vehicle.deductible || '',
    agentName: vehicle.agentName || '',
    agentPhone: vehicle.agentPhone || '',
    dotNumber: vehicle.dotNumber || '',
    mcNumber: vehicle.mcNumber || '',
    scacCode: vehicle.scacCode || '',
    iccNumber: vehicle.iccNumber || '',
    hazmatPermit: Boolean(vehicle.hazmatPermit),
    hazmatPermitExpiry: toDateInputValue(vehicle.hazmatPermitExpiry),
    engine: vehicle.engine || '',
    transmission: vehicle.transmission || '',
    fuelType: vehicle.fuelType || 'diesel',
    gvwr: vehicle.gvwr || '',
    emptyWeight: vehicle.emptyWeight || '',
    capacity: vehicle.capacity || '',
    wheelbase: vehicle.wheelbase || '',
    sleeper: Boolean(vehicle.sleeper),
    axles: vehicle.axles || 2,
    color: vehicle.color || '',
    odometer: vehicle.odometer || '',
    mileage: vehicle.mileage || vehicle.odometer || null,
    lastMaintenance: toDateInputValue(vehicle.lastMaintenance),
    nextMaintenanceDue: toDateInputValue(vehicle.nextMaintenanceDue),
    lastInspection: toDateInputValue(vehicle.lastInspection),
    nextInspectionDue: toDateInputValue(vehicle.nextInspectionDue),
    maintenanceDays: calculateDaysUntil(toIsoDateString(vehicle.nextMaintenanceDue)),
    inspectionDays: calculateDaysUntil(toIsoDateString(vehicle.nextInspectionDue)),
    assignedDriverId: vehicle.assignedDriverId || null,
    assignedDriver: assignedDriverName || 'Unassigned',
    companyId: vehicle.companyId || '',
    location: formattedLocation || 'Location unavailable',
    fuelLevel: typeof vehicle.fuelLevel === 'number' ? vehicle.fuelLevel : null,
    specifications: buildSpecifications(vehicle),
    raw: vehicle
  };
};

const prepareVehiclePayload = (formData = {}, companyId) => {
  if (!companyId) {
    throw new Error('companyId is required to create or update vehicles');
  }

  const normalizeNumber = (value) => {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const normalizeString = (value) => {
    if (value === null || value === undefined) {
      return undefined;
    }
    const trimmed = String(value).trim();
    return trimmed === '' ? undefined : trimmed;
  };

  const uppercase = (value) => {
    const base = normalizeString(value);
    return base ? base.toUpperCase() : undefined;
  };

  // Only the fields VehicleForm collects. Registration expiry, insurance, DOT/MC numbers,
  // maintenance dates and driver assignment are no longer on the form; omitting them leaves
  // any stored values untouched on update instead of re-validating stale data.
  const payload = {
    companyId,
    vehicleNumber: normalizeString(formData.vehicleNumber),
    vin: uppercase(formData.vin),
    make: normalizeString(formData.make),
    model: normalizeString(formData.model),
    year: normalizeNumber(formData.year),
    vehicleType: normalizeString(formData.type) || 'truck',
    status: normalizeString(formData.status) || 'active',
    purchaseDate: normalizeString(formData.purchaseDate),
    purchasePrice: normalizeNumber(formData.purchasePrice),
    licensePlate: uppercase(formData.licensePlate),
    plateState: uppercase(formData.plateState || formData.registrationState),
    registrationState: uppercase(formData.registrationState || formData.plateState),
    hazmatPermit: Boolean(formData.hazmatPermit),
    hazmatPermitExpiry: formData.hazmatPermit ? normalizeString(formData.hazmatPermitExpiry) : undefined,
    engine: normalizeString(formData.engine),
    transmission: normalizeString(formData.transmission),
    fuelType: normalizeString(formData.fuelType),
    gvwr: normalizeNumber(formData.gvwr),
    emptyWeight: normalizeNumber(formData.emptyWeight),
    capacity: normalizeNumber(formData.capacity),
    wheelbase: normalizeNumber(formData.wheelbase),
    sleeper: Boolean(formData.sleeper),
    axles: normalizeNumber(formData.axles),
    color: normalizeString(formData.color),
    odometer: normalizeNumber(formData.odometer)
  };

  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined) {
      delete payload[key];
    }
  });

  return payload;
};

const vehicleService = {
  async listVehicles({ companyId, params } = {}) {
    if (!companyId) {
      throw new Error('companyId is required to list vehicles');
    }

    try {
      const response = await api.get('/vehicles', {
        params,
        headers: {
          'x-company-id': companyId
        }
      });

      const data = response?.data || response;
      const vehiclesPayload = Array.isArray(data?.vehicles) ? data.vehicles : [];
      const vehicles = vehiclesPayload.map(mapVehicleFromApi);

      return {
        vehicles,
        pagination: data?.pagination || {
          total: vehicles.length,
          hasMore: false,
          lastDocId: null
        }
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async getVehicle(vehicleId, { companyId } = {}) {
    if (!vehicleId) {
      throw new Error('vehicleId is required to fetch vehicle details');
    }

    try {
      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.get(`/vehicles/${vehicleId}`, config);
      const data = response?.data || response;
      return mapVehicleFromApi(data?.vehicle || data);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async createVehicle(formData, { companyId } = {}) {
    const payload = prepareVehiclePayload(formData, companyId);

    try {
      const response = await api.post('/vehicles', payload, {
        headers: {
          'x-company-id': companyId
        }
      });

      const data = response?.data || response;
      return mapVehicleFromApi(data?.vehicle || data);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async updateVehicle(vehicleId, formData, { companyId } = {}) {
    if (!vehicleId) {
      throw new Error('vehicleId is required to update vehicle');
    }

    const payload = prepareVehiclePayload(formData, companyId);

    try {
      const response = await api.put(`/vehicles/${vehicleId}`, payload, {
        headers: {
          'x-company-id': companyId
        }
      });

      const data = response?.data || response;
      return mapVehicleFromApi(data?.vehicle || data);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async updateVehicleStatus(vehicleId, status, { companyId } = {}) {
    if (!vehicleId) {
      throw new Error('vehicleId is required to update status');
    }

    try {
      const response = await api.put(`/vehicles/${vehicleId}/status`, { status }, {
        headers: {
          'x-company-id': companyId
        }
      });

      const data = response?.data || response;
      return mapVehicleFromApi(data?.vehicle || data);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async updateDriverAssignment(vehicleId, driverId, { companyId } = {}) {
    if (!vehicleId) {
      throw new Error('vehicleId is required to manage driver assignment');
    }

    try {
      const response = await api.put(`/vehicles/${vehicleId}/assignment`, {
        driverId: driverId || null
      }, {
        headers: {
          'x-company-id': companyId
        }
      });

      const data = response?.data || response;
      return mapVehicleFromApi(data?.vehicle || data);
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async getVehicleDocuments(vehicleId, { companyId } = {}) {
    if (!vehicleId) {
      throw new Error('vehicleId is required to fetch documents');
    }

    try {
      const response = await api.get(`/vehicles/${vehicleId}/documents`, {
        headers: {
          'x-company-id': companyId
        }
      });

      const data = response?.data || response;
      return {
        documents: Array.isArray(data?.documents) ? data.documents : [],
        message: data?.message || null
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async getVehicleMaintenance(vehicleId, { companyId } = {}) {
    if (!vehicleId) {
      throw new Error('vehicleId is required to fetch maintenance records');
    }

    try {
      const response = await api.get(`/vehicles/${vehicleId}/maintenance`, {
        headers: {
          'x-company-id': companyId
        }
      });

      const data = response?.data || response;
      return {
        maintenanceRecords: Array.isArray(data?.maintenanceRecords) ? data.maintenanceRecords : [],
        message: data?.message || null
      };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async getVehicleStatistics({ companyId } = {}) {
    if (!companyId) {
      throw new Error('companyId is required to fetch vehicle statistics');
    }

    try {
      const response = await api.get('/vehicles/statistics', {
        headers: {
          'x-company-id': companyId
        }
      });

      const data = response?.data || response;
      return data?.statistics || {};
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  },

  async deleteVehicle(vehicleId, { companyId } = {}) {
    if (!vehicleId) {
      throw new Error('vehicleId is required to delete a vehicle');
    }

    try {
      const config = {};
      if (companyId) {
        config.headers = { 'x-company-id': companyId };
      }

      const response = await api.delete(`/vehicles/${vehicleId}`, config);
      return response?.data || response || { success: true };
    } catch (error) {
      throw error instanceof ApiError ? error : ApiError.fromResponse?.(error) || error;
    }
  }
};

export {
  vehicleService as default,
  mapVehicleFromApi,
  prepareVehiclePayload
};
